import React, { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import _omit from 'lodash/omit';
import { NumberParam, type QueryParamConfigMap, StringParam, withDefault } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';

import {
  GetProcessTalentsParams,
  TalentListRequestDTO,
  TalentProcessResponseDTO,
  useFilterTalents
} from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import EmptyState from '@/components/EmptyState';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import Form from '@/components/Form/Form';
import Loader from '@/components/Loader/Loader';
import TalentCard from '@/containers/Process/TalentCard';
import CreateTalentModal from '@/containers/Talents/CreateTalentModal';
import { useFolderPermissions } from '@/hooks/permissions/useFolderPermissions';
import { DEFAULT_PAGINATION, DEFAULT_SORTING, useEntityList } from '@/hooks/useEntityList';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import typography from '@/theme/typography';

const OrganizationTalents = () => {
  const { palette } = useTheme();
  const { t } = useTranslation('settings');
  const [queryBody, setQueryBody] = useState<TalentListRequestDTO | null>(null);

  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);
  const { mapStructure, location } = useOrganizationStructure(locationSlug!);

  const { value: { canCreateTalents } = {} } = useFolderPermissions({
    variables: {
      organizationSlug,
      locationSlug,
      locationType: location?.type
    }
  });

  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const { data: talentsResponse, isLoading } = useFilterTalents(
    queryBody ?? {},
    { includeStats: !queryBody?.page },
    {
      query: { suspense: false, enabled: !!queryBody }
    }
  );

  const transformRequest = useCallback(
    (request: FieldValues & { locationSlug?: string }) => {
      // eslint-disable-next-line i18next/no-literal-string
      const updatedRequest = _omit(request, ['locationSlug']);
      const location = mapStructure?.[request.locationSlug!];

      return (
        location &&
        ({
          ...updatedRequest,
          talents: {
            locations: [
              {
                slug: location.slug,
                type: location.type
              }
            ]
          }
        } satisfies TalentListRequestDTO)
      );
    },
    [mapStructure]
  );

  const { form: filterForm } = useEntityList<FieldValues, TalentListRequestDTO>(
    DEFAULT_VALUES as FieldValues,
    URL_CONFIG_MAP,
    setQueryBody,
    transformRequest,
    300
  );

  const isEmpty = !talentsResponse?.talents?.length;

  return (
    <Stack gap={1} mx={2} p={2} height="100%">
      <Form form={filterForm} sx={{ justifyContent: 'start', display: 'flex', my: 1 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, width: '100%' }}>
          <Field
            component={TextInput}
            name="q"
            innerProps={{
              InputProps: {
                sx: {
                  minWidth: 300
                },
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: 'grey.700' }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
                placeholder: t('processSettings.form.talents.searchTalentsPlaceholder')
              }
            }}
          />
          {canCreateTalents && (
            <Button
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={handleOpenCreateModal}
              sx={{ marginInlineStart: 3 }}
            >
              {t('filters:filters.new')}
            </Button>
          )}
        </Stack>
      </Form>
      <>
        {(isLoading || !queryBody) && (
          <Stack justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Stack>
        )}
        {!isLoading && !!queryBody && isEmpty && (
          <Stack justifyContent="center" height="100%">
            <EmptyState
              imageUrl="/images/empty-talent-list-illustration.svg"
              imageAlt={t('processSettings.form.talents.noTalentsTitle')}
              imageSx={{ width: 243 }}
              // TODO: add create talent logic
              // onCreate={() => {}}
              // buttonLabel={t('processSettings.form.talents.addTalent')}
            >
              <Stack alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: typography.fontWeightMedium, color: palette.grey[700], mb: 0 }}
                >
                  {t('processSettings.form.talents.noTalentsTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.grey[700] }}>
                  {t('processSettings.form.talents.noTalentsDescription')}
                </Typography>
              </Stack>
            </EmptyState>
          </Stack>
        )}
        {!isLoading &&
          !isEmpty &&
          talentsResponse?.talents?.map(talent => (
            <TalentCard
              key={talent.id}
              talent={talent as TalentProcessResponseDTO}
              showAddButton={false}
              showRemoveButton={false}
            />
          ))}
      </>
      {showCreateModal && (
        <CreateTalentModal
          onClose={handleCloseCreateModal}
          defaultOrganizationSlug={organizationSlug}
          defaultLocationSlug={locationSlug}
        />
      )}
    </Stack>
  );
};

const DEFAULT_VALUES = {
  q: '',
  ...DEFAULT_SORTING,
  ...DEFAULT_PAGINATION
} satisfies GetProcessTalentsParams;

const URL_CONFIG_MAP = {
  size: withDefault(NumberParam, DEFAULT_PAGINATION.size),
  locationSlug: withDefault(StringParam, '')
} satisfies QueryParamConfigMap;

export default OrganizationTalents;
